import { useState, useEffect } from 'react'
import { LayoutDashboard, Newspaper, Eye, CreditCard, Bookmark, TrendingUp, TrendingDown, Minus, BarChart3, PieChart as PieChartIcon, Calendar, Filter, Download, RefreshCw } from 'lucide-react'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { dashboardApi, newsApi } from '@/services/api'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { motion } from 'framer-motion'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Area, AreaChart } from 'recharts'
import axios from 'axios'
import { getAuthHeaders } from '@/lib/utils'
import { API_URL } from '@/lib/config'
import { DailyViewsCounter } from '@/components/daily-views-counter'

const CHART_COLORS = [
  'hsl(221, 83%, 53%)',  // Blue
  'hsl(142, 71%, 45%)',  // Green
  'hsl(262, 83%, 58%)',  // Purple
  'hsl(31, 97%, 52%)',   // Orange
  'hsl(339, 82%, 52%)',  // Pink
  'hsl(199, 89%, 48%)',  // Cyan
  'hsl(48, 96%, 53%)',   // Yellow
  'hsl(4, 90%, 58%)',    // Red
  'hsl(280, 67%, 60%)',  // Violet
  'hsl(173, 58%, 39%)',  // Teal
]

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload
    return (
      <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
        <p className="font-semibold text-gray-900 dark:text-gray-100">{data.feed_name}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Time: {Number(data.total_min).toFixed(2)} minutes
        </p>
        {data.articles_count && (
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Articles: {data.articles_count}
          </p>
        )}
        <p className="text-sm text-gray-500 dark:text-gray-500">
          {data.percent}% of total
        </p>
      </div>
    )
  }
  return null
}

export function DashboardOverview() {
  const [readData, setReadData] = useState(null)
  const [chartData, setChartData] = useState([])
  const [historicalData, setHistoricalData] = useState([])
  const [month, setMonth] = useState(null)
  const [loading, setLoading] = useState(true)
  const [chartType, setChartType] = useState('pie')
  const [timePeriod, setTimePeriod] = useState('current_month')
  const [refreshing, setRefreshing] = useState(false)

  // Fetch subscribed feeds count
  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const response = await dashboardApi.getSubscribedFeeds()
      return response.data
    }
  })

  // Fetch recent bookmarks (limit to 5)
  const { data: bookmarksData } = useQuery({
    queryKey: ['recentBookmarks'],
    queryFn: async () => {
      const response = await newsApi.getBookmarks(1)
      return response.data
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  })

  // Fetch reading stats
  useEffect(() => {
    fetchAllData()
  }, [timePeriod])

  const fetchAllData = async () => {
    setLoading(true)
    try {
      await Promise.all([
        fetchReadData(),
        fetchChartData(),
        fetchHistoricalData()
      ])
    } catch (error) {
      toast.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const fetchReadData = async () => {
    try {
      const headers = getAuthHeaders()
      const response = await axios.get(
        `${API_URL}/api/v1/users/feeds/read/`,
        { ...headers, params: { period: timePeriod } }
      )
      setReadData(response.data)
    } catch (error) {
      console.error('Error fetching read data:', error)
    }
  }

  const fetchChartData = async () => {
    try {
      const headers = getAuthHeaders()
      const response = await axios.get(
        `${API_URL}/api/v1/users/feeds/read/breakdown/`,
        { ...headers, params: { period: timePeriod } }
      )
      
      const breakdown = response.data.breakdown || []
      
      // Show all feeds without grouping
      const formattedData = breakdown.map((feed, index) => ({
        feed_name: feed.feed_name || 'Unknown',
        total_min: feed.total_min,
        articles_count: feed.articles_count || 0,
        avg_read_time: feed.avg_read_time || 0,
        percent: feed.percent.toFixed(2),
        fill: CHART_COLORS[index % CHART_COLORS.length]
      }))
      
      setChartData(formattedData)
      setMonth(response.data.month)
    } catch (error) {
      console.error('Error fetching chart data:', error)
    }
  }

  const fetchHistoricalData = async () => {
    try {
      const headers = getAuthHeaders()
      const response = await axios.get(
        `${API_URL}/api/v1/users/feeds/read/historical/`,
        headers
      )
      setHistoricalData(response.data.historical || [])
    } catch (error) {
      console.error('Error fetching historical data:', error)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    await fetchAllData()
    setRefreshing(false)
    toast.success('Dashboard data refreshed')
  }

  const exportData = () => {
    const dataToExport = {
      period: timePeriod,
      month: month,
      summary: readData,
      breakdown: chartData,
      historical: historicalData
    }
    
    const dataStr = JSON.stringify(dataToExport, null, 2)
    const dataBlob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(dataBlob)
    const link = document.createElement('a')
    link.href = url
    link.download = `reading-stats-${timePeriod}-${Date.now()}.json`
    link.click()
    URL.revokeObjectURL(url)
    toast.success('Data exported successfully')
  }

  const totalFeeds = subscription?.feeds?.length || 0
  const totalArticles = readData?.total_article || 0
  const totalMinutes = readData?.total_minutes || 0
  const totalPurchases = readData?.total_purchase || 0
  const totalReadTime = chartData.reduce((sum, item) => sum + item.total_min, 0)

  // Calculate trends (comparison to previous period - simplified)
  const previousArticles = readData?.previous_total_article || totalArticles
  const previousMinutes = readData?.previous_total_minutes || totalMinutes
  const previousPurchases = readData?.previous_total_purchase || totalPurchases

  const articlesTrend = totalArticles - previousArticles
  const minutesTrend = totalMinutes - previousMinutes
  const purchasesTrend = totalPurchases - previousPurchases

  const getTrendIcon = (trend) => {
    if (trend > 0) return TrendingUp
    if (trend < 0) return TrendingDown
    return Minus
  }

  const getTrendColor = (trend) => {
    if (trend > 0) return 'text-green-600 dark:text-green-400'
    if (trend < 0) return 'text-red-600 dark:text-red-400'
    return 'text-gray-600 dark:text-gray-400'
  }

  const getTrendText = (trend, unit = '') => {
    if (trend === 0) return 'No change'
    const sign = trend > 0 ? '+' : ''
    return `${sign}${trend}${unit} vs last period`
  }

  const stats = [
    {
      title: 'Total Feeds',
      value: totalFeeds,
      description: 'Number of custom feeds in your list',
      icon: Newspaper,
      color: 'text-primary',
      bgColor: 'bg-slate-50',
      trend: null, // Feeds don't have trend
      trendText: null
    },
    {
      title: 'Articles Read',
      value: totalArticles,
      description: `${Number(totalMinutes).toFixed(2)} minutes total read time`,
      icon: Eye,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      trend: articlesTrend,
      trendText: getTrendText(articlesTrend)
    },
    {
      title: 'Purchases',
      value: totalPurchases,
      description: 'Number of articles you have purchased',
      icon: CreditCard,
      color: 'text-slate-600',
      bgColor: 'bg-slate-100',
      trend: purchasesTrend,
      trendText: getTrendText(purchasesTrend)
    }
  ]

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2 flex items-center gap-3">
          <LayoutDashboard className="w-8 h-8 text-primary" />
          Dashboard Overview
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Your activity and reading statistics for this month
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon
          const TrendIcon = stat.trend !== null ? getTrendIcon(stat.trend) : null
          return (
            <motion.div
              key={stat.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium dark:text-gray-200">
                    {stat.title}
                  </CardTitle>
                  <div className={`p-2 rounded-lg ${stat.bgColor} dark:bg-opacity-20`}>
                    <Icon className={`h-4 w-4 ${stat.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold dark:text-gray-100">
                    {stat.value}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {stat.description}
                  </p>
                  {TrendIcon && (
                    <div className={`flex items-center gap-1 mt-2 text-xs ${getTrendColor(stat.trend)}`}>
                      <TrendIcon className="h-3 w-3" />
                      <span>{stat.trendText}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          )
        })}
      </div>

      {/* Daily Views Counter for Free Users */}
      <DailyViewsCounter />

      {/* Enhanced Reading Time Analytics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Reading Time Analytics
                </CardTitle>
                <CardDescription>
                  {month || 'Current Period'} • {totalReadTime.toFixed(2)} total minutes
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Select value={timePeriod} onValueChange={setTimePeriod}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current_month">This Month</SelectItem>
                    <SelectItem value="last_month">Last Month</SelectItem>
                    <SelectItem value="last_3_months">Last 3 Months</SelectItem>
                    <SelectItem value="last_6_months">Last 6 Months</SelectItem>
                    <SelectItem value="current_year">This Year</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={refreshing}
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={exportData}
                >
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Tabs value={chartType} onValueChange={setChartType} className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="pie" className="flex items-center gap-2">
                  <PieChartIcon className="w-4 h-4" />
                  Pie
                </TabsTrigger>
                <TabsTrigger value="bar" className="flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" />
                  Bar
                </TabsTrigger>
                <TabsTrigger value="line" className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" />
                  Trend
                </TabsTrigger>
                <TabsTrigger value="area" className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Area
                </TabsTrigger>
              </TabsList>
              
              {loading ? (
                <div className="mt-6 space-y-4">
                  {/* Skeleton loader for chart */}
                  <div className="animate-pulse">
                    <div className="h-[400px] bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  </div>
                  <div className="flex gap-4 justify-center">
                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  </div>
                </div>
              ) : chartData.length > 0 ? (
                <div className="mt-6">
                  <TabsContent value="pie" className="mt-0">
                    <div className="grid lg:grid-cols-[1fr,auto] gap-6">
                      {/* Pie Chart */}
                      <div className="flex items-center justify-center">
                        <ResponsiveContainer width="100%" height={450}>
                          <PieChart>
                            <Pie
                              data={chartData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ percent }) => parseFloat(percent) > 3 ? `${percent}%` : ''}
                              outerRadius={window.innerWidth < 768 ? 110 : 150}
                              innerRadius={window.innerWidth < 768 ? 55 : 80}
                              fill="#8884d8"
                              dataKey="total_min"
                            >
                              {chartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.fill} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      
                      {/* Custom Feed List - Scrollable */}
                      <div className="lg:w-[320px] space-y-2">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 sticky top-0 bg-white dark:bg-gray-900 py-2 z-10">All Feeds ({chartData.length})</h3>
                        <div className="space-y-1.5 max-h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600 scrollbar-track-transparent">
                          {chartData.map((feed, index) => (
                            <div key={index} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group">
                              <div 
                                className="w-3.5 h-3.5 rounded flex-shrink-0 shadow-sm" 
                                style={{ backgroundColor: feed.fill }}
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate group-hover:text-primary transition-colors">
                                  {feed.feed_name}
                                </p>
                                <p className="text-[10px] text-gray-500 dark:text-gray-400">
                                  {Number(feed.total_min).toFixed(2)} min • {feed.percent}% • {feed.articles_count} articles
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="bar" className="mt-0">
                    <div className="overflow-x-auto">
                      <ResponsiveContainer width="100%" height={400} minWidth={chartData.length * 80}>
                        <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: window.innerWidth < 768 ? 80 : 100 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                          <XAxis 
                            dataKey="feed_name" 
                            angle={-45}
                            textAnchor="end"
                            height={window.innerWidth < 768 ? 80 : 100}
                            fontSize={window.innerWidth < 768 ? 10 : 12}
                            tick={{ fill: 'currentColor' }}
                            className="text-gray-700 dark:text-gray-300"
                          />
                          <YAxis 
                            label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }}
                            tick={{ fill: 'currentColor' }}
                            className="text-gray-700 dark:text-gray-300"
                          />
                          <Tooltip 
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload
                                return (
                                  <div className="bg-white dark:bg-gray-800 p-3 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg">
                                    <p className="font-semibold text-gray-900 dark:text-gray-100">{data.feed_name}</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Reading Time: {Number(data.total_min).toFixed(2)} minutes</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Articles: {data.articles_count}</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">Avg per Article: {data.avg_read_time} min</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-500">{data.percent}% of total</p>
                                  </div>
                                )
                              }
                              return null
                            }}
                          />
                          <Bar dataKey="total_min" fill="hsl(221, 83%, 53%)" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="line" className="mt-0">
                    {!loading && historicalData && historicalData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={historicalData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fill: 'currentColor', fontSize: window.innerWidth < 768 ? 10 : 12 }}
                            className="text-gray-700 dark:text-gray-300"
                          />
                          <YAxis 
                            label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }}
                            tick={{ fill: 'currentColor' }}
                            className="text-gray-700 dark:text-gray-300"
                          />
                          <Tooltip 
                            labelFormatter={(value) => `Date: ${value}`}
                            formatter={(value) => [`${value} minutes`, 'Reading Time']}
                            contentStyle={{ backgroundColor: 'var(--tooltip-bg)', borderColor: 'var(--tooltip-border)' }}
                          />
                          <Line 
                            type="monotone" 
                            dataKey="total_minutes" 
                            stroke="hsl(221, 83%, 53%)" 
                            strokeWidth={window.innerWidth < 768 ? 2 : 3}
                            dot={{ fill: 'hsl(221, 83%, 53%)', strokeWidth: 2, r: window.innerWidth < 768 ? 3 : 4 }}
                            activeDot={{ r: window.innerWidth < 768 ? 5 : 6, stroke: 'hsl(221, 83%, 53%)', strokeWidth: 2 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : !loading ? (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                        <TrendingUp className="w-12 h-12 mb-4 opacity-50" />
                        <p className="text-lg font-medium">No historical data available</p>
                        <p className="text-sm">Historical trends will appear here after you start reading</p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                      </div>
                    )}
                  </TabsContent>
                  
                  <TabsContent value="area" className="mt-0">
                    {!loading && historicalData && historicalData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={400}>
                        <AreaChart data={historicalData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                          <defs>
                            <linearGradient id="colorMinutes" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="hsl(221, 83%, 53%)" stopOpacity={0.8}/>
                              <stop offset="95%" stopColor="hsl(221, 83%, 53%)" stopOpacity={0.1}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
                          <XAxis 
                            dataKey="date"
                            tick={{ fill: 'currentColor', fontSize: window.innerWidth < 768 ? 10 : 12 }}
                            className="text-gray-700 dark:text-gray-300"
                          />
                          <YAxis 
                            label={{ value: 'Minutes', angle: -90, position: 'insideLeft' }}
                            tick={{ fill: 'currentColor' }}
                            className="text-gray-700 dark:text-gray-300"
                          />
                          <Tooltip 
                            labelFormatter={(value) => `Date: ${value}`}
                            formatter={(value) => [`${value} minutes`, 'Reading Time']}
                            contentStyle={{ backgroundColor: 'var(--tooltip-bg)', borderColor: 'var(--tooltip-border)' }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="total_minutes" 
                            stroke="hsl(221, 83%, 53%)" 
                            fill="url(#colorMinutes)"
                            strokeWidth={2}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : !loading ? (
                      <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                        <Calendar className="w-12 h-12 mb-4 opacity-50" />
                        <p className="text-lg font-medium">No historical data available</p>
                        <p className="text-sm">Area chart will display your reading patterns over time</p>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                      </div>
                    )}
                  </TabsContent>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500 dark:text-gray-400 mt-6">
                  <Eye className="w-12 h-12 mb-4 opacity-50" />
                  <p className="text-lg font-medium">No reading data available</p>
                  <p className="text-sm mb-4">Start reading articles to see your statistics</p>
                  <Button 
                    variant="outline" 
                    onClick={() => window.location.href = '/'}
                    className="mt-2"
                  >
                    Browse Articles
                  </Button>
                </div>
              )}
            </Tabs>
          </CardContent>
          {chartData.length > 0 && (
            <CardFooter className="flex-col gap-2 text-sm">
              <div className="flex items-center gap-2 font-medium leading-none">
                Total read time {totalReadTime.toFixed(2)} minutes
                <TrendingUp className="h-4 w-4" />
              </div>
              <div className="leading-none text-gray-500">
                Showing total read time based on your feeds this month
              </div>
            </CardFooter>
          )}
        </Card>
      </motion.div>
      
      {/* Reading Insights Cards */}
      {chartData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">Most Read Feed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {chartData[0]?.feed_name || 'N/A'}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {Number(chartData[0]?.total_min || 0).toFixed(2)} minutes ({chartData[0]?.percent || 0}%)
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">Average per Feed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {(totalReadTime / chartData.length).toFixed(2)} min
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Across {chartData.length} feeds
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">Reading Diversity</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {chartData.length > 1 ? 'High' : 'Low'}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {chartData.length} different sources
              </p>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Bookmarks Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
      >
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Bookmark className="w-5 h-5 text-primary" />
                  Recent Bookmarks
                </CardTitle>
                <CardDescription>
                  Your recently saved articles
                </CardDescription>
              </div>
              {bookmarksData?.results && bookmarksData.results.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.location.href = '/bookmarks'}
                >
                  View All
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {bookmarksData?.results && bookmarksData.results.length > 0 ? (
              <div className="space-y-4">
                {bookmarksData.results.slice(0, 5).map((article, index) => (
                  <motion.div
                    key={article.slug}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-start gap-4 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                    onClick={() => window.location.href = `/posts/${article.slug}`}
                  >
                    {article.cover_image && (
                      <img
                        src={article.cover_image}
                        alt={article.title}
                        className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 line-clamp-2 mb-1">
                        {article.title}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                        {article.feeder?.display_name || article.feeder?.name}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                        {new Date(article.published_at).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Bookmark className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium mb-2">No bookmarks yet</p>
                <p className="text-sm mb-4">Bookmark articles to save them for later reading</p>
                <Button 
                  variant="outline" 
                  onClick={() => window.location.href = '/'}
                  className="mt-2"
                >
                  Explore Articles
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
